import React, {useEffect} from 'react';
import {useQuery, useQueryClient} from 'react-query';
import {Alert, AlertTitle, Button, Dialog, DialogTitle, Divider, Grid, Typography} from '@mui/material';
import {Formik} from 'formik';
import * as Yup from 'yup';
import IyoAuth from '../../../auth/Auth';
import ProviderHooks from './ProviderHooks';
import ProviderChildCategoryService from '../../../apis/ProviderChildCategoryService';
import IyoFormikTextField from '../../common/IyoFormikTextField';
import IyoFormikDropDown from '../../common/IyoFormikDropDown';
import IyoFormikCodeEditor from '../../common/IyoFormikCodeEditor';
import IyoFormSection from '../../common/IyoFormSection';
import DataStreamDialogHelpers from '../data-streams/data-stream-edit-dialog/helpers';
import {isValidRe2Syntax, toJsParseable, unsupportedByRe2} from '../../../util/util';

// How a provider's rows are normally split out to child owners. Values match the match
// types in data_stream.partition_configs and the dbt macros.
//
// A provider may name more than one. The column stores them comma-separated, and the data
// stream editor then offers a match type per scheme so one stream can carry rules of each
// kind — EZfeed-Loads is the live case, splitting by pen and by label. The dbt resolvers
// each read only their own match_type, so they already coexist on one stream.
//
// reassign is absent by design: it is a per-stream override, not a provider scheme, and
// is offered on every stream regardless of what is set here.
//
//   PEN          - the child owns pens in the pen dimension; rows in those pens are theirs
//   LABEL        - a column on the row carries the child's label
//   FILE_PATTERN - the source file name is matched against a regex held per rule on the
//                  data stream, so nothing is needed at provider level
//   FILE_KEY     - a key is extracted from the source file name by the regex in
//                  partition_column, then matched against the rules on the data stream
const PARTITION_SCHEMES = [
  {name: 'PEN', value: 'PEN'},
  {name: 'LABEL', value: 'LABEL'},
  {name: 'FILE_PATTERN', value: 'FILE_PATTERN'},
  {name: 'FILE_KEY', value: 'FILE_KEY'},
];

// Schemes that need a value in partition_column, and what that value means to each.
const SCHEMES_NEEDING_COLUMN = ['LABEL', 'FILE_KEY'];

// Of those, the ones whose value is a regular expression rather than a column name.
const SCHEMES_WITH_REGEX_COLUMN = ['FILE_KEY'];

// Field names as the form labels them, for the list of what is blocking Save.
const FIELD_LABELS = {
  name: 'Name',
  description: 'Description',
  display_name: 'Display Name',
  provider_child_category_id: 'Sub-Category',
  schema: 'JSON Schema',
  properties: 'Properties',
  has_hwm: 'Has HWM',
  hwm_format: 'HWM Format',
  device_provider: 'Device Provider',
  v3: 'V3',
  multi_tenant_enabled: 'Multi Tenant Enabled',
  partition_scheme: 'Partition Scheme',
  partition_column: 'Partition Column',
};

const PARTITION_COLUMN_LABELS = {
  LABEL: 'Partition Column',
  FILE_KEY: 'File Name Pattern (one capture group)',
};

// The form works in arrays; the column is a comma-separated string. Parsing keeps tokens
// exactly as stored, so a value this build does not recognise survives a round trip
// instead of being wiped by an edit to some unrelated field.
const schemeList = (value) => DataStreamDialogHelpers.parsePartitionSchemes(value);
const schemeString = (list) => schemeList(list).join(',');

// Of the selected schemes, the one that owns partition_column. LABEL and FILE_KEY both
// claim it and cannot be combined; validate() reports that rather than picking silently.
const columnOwningScheme = (list) => schemeList(list).find((s) => SCHEMES_NEEDING_COLUMN.includes(s));

// Counts capturing groups by alternating with an empty branch so the pattern matches ''.
// Normalised first so RE2-only spellings like (?P<key>...) count rather than throw.
const countCaptureGroups = (pattern) => {
  try {
    return new RegExp(`${toJsParseable(pattern)}|`).exec('').length - 1;
  } catch (err) {
    return 0;
  }
};

// What a PC Agent provider is not free to choose.
//
// description - its own name. Every one of them in the database is stored that way, and the
//               agent shows the description to the operator as the source's label, where a
//               second wording just reads as a different source.
// has_hwm     - false. The agent hands over whole files and deletes them behind itself;
//               there is no cursor into a remote system for a high water mark to hold.
// v3          - false. v3 pulls from an API on a schedule. Nothing pulls from an agent, so
//               a PC Agent provider has no v3 form to run in.
//
// Applied here rather than only on save, so the greyed-out fields show what will be written.
const DEVICE_PROVIDER_FIXED = {has_hwm: 'false', v3: 'false'};

// Written in one setValues rather than a setFieldValue per field. Each setFieldValue
// revalidates against the values as they were at the last render, so the second and third
// call would each validate a form still missing the other two changes - and the last to
// resolve wins. That left Save disabled saying Has HWM was Required, until some later
// render happened to revalidate the form as it actually stood.
const DeviceProviderDefaults = ({values, setValues}) => {
  useEffect(() => {
    const fixed = {description: values.name, ...DEVICE_PROVIDER_FIXED};
    const changes = Object.entries(fixed).filter(([field, value]) => values[field] !== value);

    if (!changes.length) return;

    setValues({...values, ...Object.fromEntries(changes)});
  }, [values, setValues]);

  return null;
};

const ProviderEditDialog = ({state, setState}) => {
  const queryClient = useQueryClient();

  // ####################[ Configs ]####################
  const DataTypeName = 'Provider';

  const trueFalseDataList = [
    {name: 'true', value: 'true'},
    {name: 'false', value: 'false'}
  ];

  const hwmFormatDataList = [
    {id: 0, name: 'YYYY-MM-DD hh:mm:ss.SSS', value: 'YYYY-MM-DD hh:mm:ss.SSS'},
    {id: 1, name: 'YYYY-MM-DD hh:mm:ss', value: 'YYYY-MM-DD hh:mm:ss'},
    {id: 2, name: 'YYYY-MM-DDThh:mm:ssZ (UTC)', value: 'YYYY-MM-DDThh:mm:ssZ'},
    {id: 3, name: 'YYYY-MM-DD', value: 'YYYY-MM-DD'}
  ];

  // ####################[ Helpers ]####################
  const produceError = (error, setState) => {
    const message = `${error.message}` +
      `${error.response?.data ? ': ' + JSON.stringify(error.response.data) : ''}`;

    setState.setErrorOpen(false);
    setState.setErrDisplayText(message);
    setState.setErrorOpen(true);

    console.error(message);
  };

  const generateValidationSchema = (values) => {
    const schema = {
      name: Yup.string().required('Required'),
      schema: Yup.string().required('Required'),
      properties: Yup.string().required('Required'),
      has_hwm: Yup.string().required('Required'),
      device_provider: Yup.string().required('Required'),
      multi_tenant_enabled: Yup.string().required('Required'),
    };

    // partition_scheme is only required when multi_tenant_enabled is set to true. It holds
    // a list: a provider may split its rows more than one way.
    schema.partition_scheme = Yup.array().when('multi_tenant_enabled', {
      is: (val) => val === 'true',
      then: Yup.array()
        .of(Yup.string().oneOf(PARTITION_SCHEMES.map(s => s.value), `Must be one of ${PARTITION_SCHEMES.map(s => s.value).join(', ')}`))
        .min(1, 'Required'),
      otherwise: Yup.array().notRequired()
    });

    // partition_column holds the column name for LABEL and the extraction regex for
    // FILE_KEY. PEN and FILE_PATTERN need nothing here.
    schema.partition_column = Yup.string().when('partition_scheme', {
      is: (val) => Boolean(columnOwningScheme(val)),
      then: Yup.string()
        .required('Required')
        // Only the regex schemes are checked; for LABEL this field is a column name.
        .test('valid-regex', 'Not a valid regular expression.', function (value) {
          if (!schemeList(this.parent.partition_scheme).some((s) => SCHEMES_WITH_REGEX_COLUMN.includes(s)) || !value) return true;
          return isValidRe2Syntax(value);
        })
        .test(
          'single-capture-group',
          'Must contain exactly one capture group, e.g. -([0-9]+)_',
          function (value) {
            if (!schemeList(this.parent.partition_scheme).some((s) => SCHEMES_WITH_REGEX_COLUMN.includes(s)) || !value) return true;
            // An invalid pattern is already reported above; do not fail twice for it.
            if (!isValidRe2Syntax(value)) return true;
            return countCaptureGroups(value) === 1;
          }
        )
        // Message comes from createError below, naming the construct.
        .test('re2-supported', 'Not supported by BigQuery.', function (value) {
          if (!schemeList(this.parent.partition_scheme).some((s) => SCHEMES_WITH_REGEX_COLUMN.includes(s)) || !value) return true;
          if (!isValidRe2Syntax(value)) return true;
          const problem = unsupportedByRe2(value);
          return problem ? this.createError({message: problem}) : true;
        }),
      otherwise: Yup.string().notRequired()
    });

    // cron, concurrency_limit, backoff_interval and timezone_sensitive are not shown and
    // not validated: nothing reads them. Their columns still exist and existing values are
    // left exactly as they are, so nothing here can be the reason a provider will not save.

    return schema;
  };

  const cleanPost = async (values) => {
    let body = {};

    for (let field in values)
      if (values[field] !== '')
        body[field] = values[field];

    // The form holds the schemes as an array; the column is a comma-separated string. An
    // empty selection is no scheme at all, not an empty array.
    const schemes = schemeString(values.partition_scheme);
    if (schemes)
      body['partition_scheme'] = schemes;
    else
      delete body['partition_scheme'];

    const boolFields = ['has_hwm', 'device_provider', 'timezone_sensitive', 'v3', 'multi_tenant_enabled'];
    for (let field of boolFields) {
      if (body[field] === 'true')
        body[field] = true;
      else if (body[field] === 'false')
        body[field] = false;
    }

    if (values.timezone_sensitive === '')
      body['timezone_sensitive'] = false;  // Because it's required in the DB but shouldn't be

    const jsonFields = ['schema', 'properties'];
    for (let field of jsonFields)
      body[field] = JSON.parse(body[field]);

    const numFields = ['concurrency_limit', 'backoff_interval'];
    for (let field of numFields)
      if (body[field])
        body[field] = Number(body[field]);

    if (body['has_hwm'] === true && !body['hwm_format'])
      body['hwm_format'] = 'YYYY-MM-DD hh:mm:ss.SSS';
    else if (body['has_hwm'] === false && body['hwm_format'])
      body['hwm_format'] = null;

    if (body['device_provider'] === true) {
      body['cron'] = null;
      body['concurrency_limit'] = null;
      body['backoff_interval'] = null;
      body['has_hwm'] = false;
      body['hwm_format'] = null;
      body['v3'] = false;
    }

    body['created_by'] = await IyoAuth.getUser();
    body['updated_by'] = await IyoAuth.getUser();

    return body;
  };

  const cleanPut = async (values) => {
    let body = {};

    for (let field in values)
      if (values[field] !== state.rowEditing[field])
        body[field] = values[field] === '' ? null : values[field];

    // An array never compares equal to the stored string, so the loop above always picks
    // partition_scheme up. Compare the joined form instead and drop it when unchanged,
    // or an edit to any other field would rewrite the column for no reason.
    const schemes = schemeString(values.partition_scheme);
    if (schemes === (state.rowEditing.partition_scheme ?? ''))
      delete body['partition_scheme'];
    else
      body['partition_scheme'] = schemes === '' ? null : schemes;

    if (Object.entries(body).length === 0)
      return undefined;

    body['id'] = state.rowEditing.id;
    body['updated_by'] = await IyoAuth.getUser();

    const boolFields = ['has_hwm', 'device_provider', 'timezone_sensitive', 'v3', 'multi_tenant_enabled'];
    for (let field of boolFields) {
      if (body[field] === 'true')
        body[field] = true;
      else if (body[field] === 'false')
        body[field] = false;
    }

    const jsonFields = ['schema', 'properties'];
    for (let field of jsonFields)
      if (body[field])
        body[field] = JSON.parse(body[field]);

    if (body['has_hwm'] === true) {
      if (values.hwm_format === '')
        body['hwm_format'] = 'YYYY-MM-DD hh:mm:ss.SSS';
      else
        body['hwm_format'] = values.hwm_format;
    }
    else if (body['has_hwm'] === false)
      body['hwm_format'] = null;

    if (body['device_provider'] === true) {
      body['cron'] = null;
      body['concurrency_limit'] = null;
      body['backoff_interval'] = null;
      body['has_hwm'] = false;
      body['hwm_format'] = null;
      body['v3'] = false;
    }

    return body;
  };

  // ####################[ Handlers and Queries ]####################
  const pageFn = {
    handleCreateSubmit: async (values, postProvider, state, setState) => {
      const body = await cleanPost(values);
      await postProvider.mutate(body);
      setState.setEditOpen(false);
    },
    handleEditSubmit: async (values, queryClient, putProvider, state, setState) => {
      const body = await cleanPut(values);
      if (!body)
        setState.setEditOpen(false);
      else {
        await putProvider.mutate(body);
        setState.setEditOpen(false);
      }
    },
    handleClose: async (setState) => {
      setState.setEditOpen(false);
    },
    validate: async (values) => {
      const validationObject = generateValidationSchema(values);

      let errors = {};

      // Validated as one object rather than field by field. Every .when() above reads a
      // sibling field, and a field validated on its own has no siblings to read: the
      // partition rules resolved against nothing and so never fired, letting a multi-tenant
      // provider save with no scheme and a FILE_KEY provider save with no pattern.
      try {
        await Yup.object().shape(validationObject).validate(values, {abortEarly: false});
      } catch (err) {
        // abortEarly false reports every failure in err.inner; a cast failure arrives on
        // err itself with no inner list.
        const issues = err.inner?.length ? err.inner : [err];
        for (const issue of issues)
          if (issue.path && !errors[issue.path])
            errors[issue.path] = issue.message;
      }

      // Yup doesn't have a JSON string validation option (at least not one that works)
      const boolFields = ['schema', 'properties'];
      for (let field of boolFields) {
        try {
          JSON.parse(values[field])
        }
        catch (err) {
          if (errors[field] !== 'Required')
            errors[field] = 'Invalid JSON - ' + err.toString();
        }
      }

      // Enforced at save as well as in the field above, so a provider whose description was
      // already something else cannot keep it by way of an edit to an unrelated field.
      if (DataStreamDialogHelpers.isDeviceProvider(values) && values.description !== values.name)
        errors.description = 'Must match the name for a PC Agent provider.';

      // LABEL and FILE_KEY both take their meaning from partition_column, which can only
      // hold one thing. Every other combination is fine — the match types are independent
      // downstream.
      const columnSchemes = schemeList(values.partition_scheme)
        .filter((scheme) => SCHEMES_NEEDING_COLUMN.includes(scheme));
      if (columnSchemes.length > 1)
        errors.partition_scheme = `${columnSchemes.join(' and ')} both use Partition Column; pick one.`;

      return errors;
    },
    useInitialValuesQuery: (state, setState) => {
      return useQuery(['getInitialProviderValues', state.providerId, state.rowEditing], () => {
          const fields = [
            'name',
            'description',
            'display_name',
            'provider_child_category_id',
            'schema',
            'properties',
            'has_hwm',
            'hwm_format',
            'device_provider',
            'cron',
            'concurrency_limit',
            'backoff_interval',
            'timezone_sensitive',
            'v3',
            'multi_tenant_enabled',
            'partition_scheme',
            'partition_column',
          ];

          let values = {};

          for (let field of fields)
            values[field] = state.rowEditing?.[field] ?? '';

          // Stored as a comma-separated string, edited as a multi-select.
          values['partition_scheme'] = schemeList(state.rowEditing?.partition_scheme);

          // Put in default values to help the user
          if (state.editMode === 'create') {
            values['schema'] = JSON.stringify({
              type: 'object',
              title: 'Example',
              required: ['example'],
              properties: {example: {type: 'string', description: 'an example field'}},
              sensitiveValues: [],
              additionalProperties: false
            }, null , 2);

            values['properties'] = JSON.stringify({
              example: 'example field'
            }, null, 2);

            values['cron'] = '0 6 * * ? *';

            values['concurrency_limit'] = 10;

            values['backoff_interval'] = 2;

            values['multi_tenant_enabled'] = 'false';

            values['partition_scheme'] = [];

            values['partition_column'] = '';
          }

          return values;
        },
        {
          onError: (error) => {
            produceError(error, setState);
          },
          staleTime: Infinity,
        }
      )
    }
  };

  const getInitialValues = pageFn.useInitialValuesQuery(state, setState);

  // Sub-Category options for the Identity section. A blank entry is included so a provider
  // can be left uncategorised - 36 of the 92 seeded providers have no child category, and
  // the column is nullable.
  const getChildCategories = useQuery('getProviderChildCategoriesPage',
    async () => ProviderChildCategoryService.getAll(),
    {retry: false, staleTime: Infinity}
  );
  const childCategoryOptions = [{name: '(none)', value: ''}].concat(
    (getChildCategories.data || [])
      .filter((cat) => cat.active)
      .map((cat) => ({name: cat.name, value: cat.id}))
      .sort((a, b) => a.name.localeCompare(b.name))
  );

  const putProvider = ProviderHooks.useProviderQuery('PUT', queryClient, state, setState,
    produceError);
  const postProvider = ProviderHooks.useProviderQuery('POST', queryClient, state, setState,
    produceError)

  // ####################[ Component ]####################
  return (
    <Dialog
      open={state.editOpen}
      onClose={() => pageFn.handleClose(setState)}
      maxWidth='md'
      fullWidth={true}
    >
      <DialogTitle>
        {
          state.editMode === 'edit' ? `Edit ${DataTypeName} : id = ${state.rowEditing.id}` :
            state.editMode === 'create' ? `Create ${DataTypeName}` : ''
        }
      </DialogTitle>
      {
        !getInitialValues.data ?
          null :
          <Formik
            initialValues={getInitialValues.data}

            validate={values => pageFn.validate(values)}

            onSubmit={state.editMode === 'edit' ? values => pageFn.handleEditSubmit(values, queryClient,
                putProvider, state, setState) :
              state.editMode === 'create' ? values => pageFn.handleCreateSubmit(values, postProvider,
                  state, setState) :
                () => {}}
          >
            {({
                errors,
                handleBlur,
                handleChange,
                handleSubmit,
                isSubmitting,
                resetForm,
                setValues,
                touched,
                values
              }) => (
              <form onSubmit={handleSubmit}>
                {DataStreamDialogHelpers.isDeviceProvider(values) && (
                  <DeviceProviderDefaults values={values} setValues={setValues}/>
                )}
                <div style={{margin: '2%'}}>
                  <Grid container rowSpacing={0} columnSpacing={2}>
                    {/* ##########################[ Identity ]##############################
                        A PC Agent provider describes itself by its own name, so the
                        description is fixed and greyed for one of those. */}
                    <Grid item xs={12}>
                      <IyoFormSection title='Identity' description='What this provider is called.'>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <IyoFormikTextField
                              name='name'
                              label='Name'
                              values={values}
                              touched={touched}
                              errors={errors}
                              handleBlur={handleBlur}
                              handleChange={handleChange}
                              required
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <IyoFormikTextField
                              name='description'
                              label='Description'
                              values={values}
                              touched={touched}
                              errors={errors}
                              handleBlur={handleBlur}
                              handleChange={handleChange}
                              disabled={DataStreamDialogHelpers.isDeviceProvider(values)}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <IyoFormikTextField
                              name='display_name'
                              label='Display Name'
                              values={values}
                              touched={touched}
                              errors={errors}
                              handleBlur={handleBlur}
                              handleChange={handleChange}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <IyoFormikDropDown
                              name='provider_child_category_id'
                              label='Sub-Category'
                              dataList={childCategoryOptions}
                              values={values}
                              touched={touched}
                              errors={errors}
                              handleBlur={handleBlur}
                              handleChange={handleChange}
                            />
                          </Grid>
                        </Grid>
                      </IyoFormSection>
                    </Grid>
                    {/* ##########################[ Schema ]############################## */}
                    <Grid item xs={12} sx={{mt: 2}}>
                      <IyoFormSection
                        title='Schema'
                        description={'The JSON schema a stream\'s properties are validated against, and the ' +
                          'defaults every new stream starts from.'}
                      >
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <IyoFormikCodeEditor
                              name='schema'
                              label='Schema'
                              values={values}
                              touched={touched}
                              errors={errors}
                              handleBlur={handleBlur}
                              handleChange={handleChange}
                              rows={15}
                              required
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <IyoFormikCodeEditor
                              name='properties'
                              label='Properties'
                              values={values}
                              touched={touched}
                              errors={errors}
                              handleBlur={handleBlur}
                              handleChange={handleChange}
                              rows={5}
                              required
                            />
                          </Grid>
                        </Grid>
                      </IyoFormSection>
                    </Grid>
                    {/* ##########################[ Extraction ]##############################
                        How the pipeline gets at this provider's data. A PC Agent provider
                        settles all three for itself: the agent pushes whole files, so there
                        is no watermark to hold and nothing for v3 to pull. */}
                    <Grid item xs={12} sx={{mt: 2}}>
                      <IyoFormSection title='Extraction' description='How the pipeline reads from this provider.'>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <IyoFormikDropDown
                              name='device_provider'
                              label='Device Provider'
                              dataList={trueFalseDataList}
                              values={values}
                              touched={touched}
                              errors={errors}
                              handleBlur={handleBlur}
                              handleChange={handleChange}
                              required
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <IyoFormikDropDown
                              name='v3'
                              label='Version 3'
                              dataList={trueFalseDataList}
                              values={values}
                              touched={touched}
                              errors={errors}
                              handleBlur={handleBlur}
                              handleChange={handleChange}
                              required
                              disabled={DataStreamDialogHelpers.isDeviceProvider(values)}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <IyoFormikDropDown
                              name='has_hwm'
                              label='Has HWM'
                              dataList={trueFalseDataList}
                              values={values}
                              touched={touched}
                              errors={errors}
                              handleBlur={handleBlur}
                              handleChange={handleChange}
                              required
                              disabled={DataStreamDialogHelpers.isDeviceProvider(values)}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <IyoFormikDropDown
                              name='hwm_format'
                              label='HWM Format'
                              dataList={hwmFormatDataList}
                              values={values}
                              touched={touched}
                              errors={errors}
                              handleBlur={handleBlur}
                              handleChange={handleChange}
                              disabled={values.has_hwm === 'false'}
                            />
                          </Grid>
                        </Grid>
                      </IyoFormSection>
                    </Grid>
                    {/* ##########################[ Multi Tenancy ]############################## */}
                    <Grid item xs={12} sx={{mt: 2}}>
                      <IyoFormSection
                        title='Multi tenancy'
                        description='How one stream from this provider is split out to child owners.'
                      >
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <IyoFormikDropDown
                              name='multi_tenant_enabled'
                              label='Multi Tenant Enabled'
                              dataList={trueFalseDataList}
                              values={values}
                              touched={touched}
                              errors={errors}
                              handleBlur={handleBlur}
                              handleChange={handleChange}
                              required
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <IyoFormikDropDown
                              name='partition_scheme'
                              label='Partition Scheme'
                              dataList={PARTITION_SCHEMES}
                              values={values}
                              touched={touched}
                              errors={errors}
                              handleBlur={handleBlur}
                              multiple
                              // Clear the column when the scheme that owns it changes: its
                              // meaning differs per scheme, so a column name left over from
                              // LABEL is not a valid regex for FILE_KEY. Adding a scheme that
                              // does not use the column — PEN alongside LABEL — leaves it alone.
                              //
                              // Both go out in one setValues. handleChange followed by a
                              // setFieldValue revalidates twice, the second time against the
                              // scheme as it was before the change, so the rules that read one
                              // field off the other judged a form that never existed.
                              handleChange={(e) => {
                                const partition_scheme = e.target.value;
                                const columnChanged =
                                  columnOwningScheme(partition_scheme) !== columnOwningScheme(values.partition_scheme);

                                setValues({
                                  ...values,
                                  partition_scheme,
                                  ...(columnChanged ? {partition_column: ''} : {})
                                });
                              }}
                              required={values.multi_tenant_enabled === 'true'}
                              disabled={values.multi_tenant_enabled === 'false'}
                            />
                          </Grid>
                          {/* Only rendered for the schemes that take a value. PEN reads its
                              mapping from the pen dimension and FILE_PATTERN keeps its regex
                              per rule on the data stream, so showing an input for either just
                              invites a value that is silently ignored. A provider selecting
                              several schemes shows the field for whichever one claims it. */}
                          {columnOwningScheme(values.partition_scheme) && (
                            <Grid item xs={12}>
                              <IyoFormikTextField
                                name='partition_column'
                                label={PARTITION_COLUMN_LABELS[columnOwningScheme(values.partition_scheme)]}
                                values={values}
                                touched={touched}
                                errors={errors}
                                handleBlur={handleBlur}
                                handleChange={handleChange}
                                required
                              />
                            </Grid>
                          )}
                        </Grid>
                      </IyoFormSection>
                    </Grid>
                    {/* ##########################[ Blockers ]##############################
                        Save is disabled by any error in the form, but a field only shows its
                        error once touched - so a value that was already invalid when the
                        dialog opened disabled Save and said nothing. Naming them here means
                        nobody has to guess, or talk the form round by setting something
                        untrue. */}
                    {Object.keys(errors).length > 0 && (
                      <Grid item xs={12} sx={{mt: 2}}>
                        <Alert severity='warning'>
                          <AlertTitle>Save is disabled until these are fixed</AlertTitle>
                          {Object.keys(errors).map((field) => (
                            <Typography variant='body2' key={field}>
                              {FIELD_LABELS[field] || field}: {errors[field]}
                            </Typography>
                          ))}
                        </Alert>
                      </Grid>
                    )}
                    {/* ##########################[ Row Submit ]############################## */}
                    <Grid item xs={12} sx={{mt: 2}}>
                      <Divider sx={{mb: 2}}/>
                      <Button
                        color='primary'
                        disabled={isSubmitting}
                        onClick={() => pageFn.handleClose(setState)}
                        size='large'
                        type='button'
                        variant='outlined'
                        sx={{mr: 1}}
                      >
                        Cancel
                      </Button>
                      <Button
                        color='primary'
                        disabled={isSubmitting || Object.keys(errors).length !== 0}
                        size='large'
                        type='submit'
                        variant='contained'
                      >
                        Save
                      </Button>
                    </Grid>
                  </Grid>
                </div>
              </form>
            )}
          </Formik>
      }
    </Dialog>
  );
};

export default ProviderEditDialog;